import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { HeartHandshake, ArrowLeft, Phone, MessageCircle, Loader, Clock, Mail, Check } from "lucide-react";
import { toast } from "sonner";
import { es } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Foto del facilitador (Refugium) — provista por el usuario.
const FACILITATOR_PHOTO =
  "https://customer-assets.emergentagent.com/job_comunidad-duelo/artifacts/uav0sbms_IMG_4349.jpeg";

function IntroScreen({ onStart, onSeeMine }) {
  return (
    <div data-testid="session-intro" className="max-w-lg mx-auto px-5 py-8">
      <div className="text-center mb-7">
        <div className="w-16 h-16 rounded-full bg-[#E48D57]/12 flex items-center justify-center mx-auto mb-4">
          <HeartHandshake size={30} className="text-[#E48D57]" />
        </div>
        <h1 className="font-heading text-3xl text-[#5B3E2E] font-semibold mb-2">Acompañamiento individualizado</h1>
        <p className="text-xs tracking-[0.2em] uppercase text-[#5B3E2E]/40 font-semibold">Una sesión, un encuentro</p>
      </div>

      {/* Foto del facilitador */}
      <div data-testid="session-facilitator-photo" className="rounded-3xl overflow-hidden mb-5 shadow-[0_6px_24px_rgba(91,62,46,0.12)] border border-[#5B3E2E]/8">
        <img
          src={FACILITATOR_PHOTO}
          alt="Refugium — facilitador"
          className="w-full h-80 object-cover object-center"
          loading="lazy"
        />
      </div>

      <div className="bg-[#FDFAF4] rounded-3xl border border-[#5B3E2E]/8 p-6 mb-5 shadow-[0_4px_20px_rgba(91,62,46,0.05)]">
        <p className="text-[15px] text-[#5B3E2E]/85 leading-relaxed mb-4">
          Si lo que llevas dentro pesa más de lo que las salas pueden sostener, aquí estoy.
        </p>
        <p className="text-[15px] text-[#5B3E2E]/85 leading-relaxed mb-4">
          Refugium nació de mis propias heridas no contadas, de la vulnerabilidad que durante mucho tiempo no supe compartir. Por eso sé lo que es callar lo que más necesitamos decir, incluso a los que más queremos. Y también sé lo que es, por fin, encontrar un lugar donde ser escuchado sin prisa, sin juicio y sin tener que poner buena cara.
        </p>
        <p className="text-[15px] text-[#5B3E2E]/85 leading-relaxed mb-4">
          En esta sesión nos encontraremos tú y yo, <strong className="text-[#5B3E2E]">por llamada de teléfono (o por WhatsApp si estás fuera de España)</strong>, durante 30 minutos. Solo eso: tu historia, tu silencio cuando lo necesites, y mi presencia entera para acompañarte donde quieras llegar.
        </p>
        <p className="text-[15px] text-[#5B3E2E]/85 leading-relaxed">
          No tengo respuestas para todo. Pero sí tengo tiempo, escucha y la certeza de que abrir lo que duele <em>siempre</em> ayuda a sostenerlo mejor.
        </p>
      </div>

      <div className="bg-[#6B9070]/6 rounded-2xl border border-[#6B9070]/15 p-4 mb-6">
        <p className="text-[13px] text-[#6B9070] leading-relaxed text-center italic">
          “Lo que se nombra duele menos. Lo que se comparte deja de ser carga.”
        </p>
      </div>

      <Button
        data-testid="session-request-btn"
        onClick={onStart}
        className="w-full bg-[#E48D57] hover:bg-[#D07C46] text-white rounded-full py-4 text-base font-medium shadow-[0_4px_16px_rgba(217,120,51,0.25)]"
      >
        Solicitar mi sesión
      </Button>

      <button
        data-testid="session-mine-btn"
        onClick={onSeeMine}
        className="w-full text-center text-[13px] text-[#5B3E2E]/60 hover:text-[#5B3E2E] mt-4"
      >
        Ver mis sesiones solicitadas →
      </button>

      <p className="text-[11px] text-[#5B3E2E]/40 text-center mt-6">Con cariño,<br /><span className="font-medium">Refugium</span></p>
    </div>
  );
}

// Helpers de fecha
const toIsoDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function BookingFlow({ onBack, onBooked }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [modality, setModality] = useState("call");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/sessions/slots?days_ahead=60`, { withCredentials: true })
      .then(r => setSlots(r.data.slots || []))
      .catch(() => toast.error("No se pudieron cargar las franjas"))
      .finally(() => setLoading(false));
  }, []);

  // Indexar slots por día
  const slotsByDate = useMemo(() => {
    const map = new Map();
    for (const s of slots) {
      if (!map.has(s.date)) map.set(s.date, []);
      map.get(s.date).push(s);
    }
    return map;
  }, [slots]);

  // Días con al menos una franja libre (para resaltar en el calendario)
  const availableDates = useMemo(
    () => Array.from(slotsByDate.entries())
      .filter(([, items]) => items.some(i => i.available))
      .map(([date]) => {
        const [y, m, d] = date.split("-").map(Number);
        return new Date(y, m - 1, d);
      }),
    [slotsByDate]
  );

  // Días laborables (L-V) dentro de la ventana sin ninguna franja libre → "Completo" (rojo)
  const fullDates = useMemo(
    () => Array.from(slotsByDate.entries())
      .filter(([, items]) => items.length > 0 && items.every(i => !i.available))
      .map(([date]) => {
        const [y, m, d] = date.split("-").map(Number);
        return new Date(y, m - 1, d);
      }),
    [slotsByDate]
  );

  // Función que el calendario usa para desactivar días sin franja libre
  const isDateDisabled = (date) => {
    if (!date) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Antelación mínima 24 h: no se puede reservar hoy ni días anteriores.
    if (date <= today) return true;
    const wd = date.getDay(); // Dom=0 .. Sab=6
    if (wd === 0 || wd === 6) return true;
    const iso = toIsoDate(date);
    const items = slotsByDate.get(iso);
    if (!items) return true;
    return !items.some(i => i.available);
  };

  const selectedDateIso = selectedDate ? toIsoDate(selectedDate) : null;
  const selectedItems = selectedDateIso ? (slotsByDate.get(selectedDateIso) || []) : [];

  const handleSubmit = async () => {
    if (!selectedDateIso || !selectedTime) { toast.error("Elige una franja horaria"); return; }
    if (!phone.trim()) { toast.error("Indica tu número de teléfono"); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/sessions/book`, {
        date: selectedDateIso,
        time: selectedTime,
        modality,
        phone: phone.trim(),
        notes: notes.trim() || null,
      }, { withCredentials: true });
      onBooked(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo reservar");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader size={24} className="text-[#E48D57] animate-spin" /></div>;
  }

  return (
    <div data-testid="session-booking-flow" className="max-w-lg mx-auto px-5 py-6">
      <button onClick={onBack} className="flex items-center gap-2 text-[#5B3E2E]/60 hover:text-[#5B3E2E] mb-5 text-sm">
        <ArrowLeft size={16} /> Volver
      </button>

      <h2 className="font-heading text-2xl text-[#5B3E2E] mb-5">Elige tu franja</h2>

      {/* Aviso visible: contactar si no hay horarios que encajen */}
      <div data-testid="session-email-cta" className="bg-[#E48D57]/8 rounded-2xl border border-[#E48D57]/25 p-4 mb-5 flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-[#E48D57]/15 flex items-center justify-center flex-shrink-0">
          <Mail size={16} className="text-[#E48D57]" />
        </div>
        <div className="flex-1">
          <p className="text-[13px] text-[#5B3E2E] font-medium leading-snug mb-1">
            ¿No te encajan los horarios disponibles?
          </p>
          <p className="text-[12px] text-[#5B3E2E]/70 leading-relaxed">
            Escríbenos a{" "}
            <a
              href="mailto:espacio_refugium@outlook.com?subject=Solicitud%20de%20sesi%C3%B3n%20individualizada"
              className="text-[#E48D57] underline font-medium break-all"
            >
              espacio_refugium@outlook.com
            </a>{" "}
            y trataremos de buscar juntos una franja a tu medida.
          </p>
        </div>
      </div>

      {/* Calendario */}
      <div className="bg-[#FDFAF4] rounded-3xl border border-[#5B3E2E]/10 p-3 sm:p-4 mb-5">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(d) => { if (d) { setSelectedDate(d); setSelectedTime(null); } }}
          month={currentMonth}
          onMonthChange={setCurrentMonth}
          locale={es}
          disabled={isDateDisabled}
          modifiers={{ available: availableDates, full: fullDates }}
          modifiersClassNames={{
            available: "",
            full: "!bg-[#c44a3a]/15 !text-[#c44a3a] !opacity-100 !no-underline font-semibold rounded-full",
          }}
          className="mx-auto [&_.rdp-caption]:px-0 [&_.rdp-cell]:p-0 [&_.rdp-day]:h-9 [&_.rdp-day]:w-9 [&_.rdp-head_cell]:w-9 [&_.rdp-table]:w-full"
          classNames={{
            months: "flex flex-col",
            month: "space-y-2",
            caption: "flex justify-between items-center px-2",
            caption_label: "text-base font-heading font-semibold text-[#5B3E2E] capitalize",
            nav: "flex items-center gap-1",
            nav_button: "h-8 w-8 bg-transparent p-0 text-[#5B3E2E]/60 hover:text-[#5B3E2E] rounded-full hover:bg-[#5B3E2E]/5",
            table: "w-full border-collapse",
            head_row: "flex justify-around",
            head_cell: "text-[#5B3E2E]/40 text-xs font-medium w-9 text-center",
            row: "flex justify-around mt-1",
            cell: "text-center p-0",
            day: "h-9 w-9 text-sm font-medium rounded-full hover:bg-[#5B3E2E]/5 text-[#5B3E2E] transition-colors flex items-center justify-center",
            day_selected: "!bg-[#95B89A] !text-white hover:!bg-[#95B89A]",
            day_today: "font-bold text-[#E48D57]",
            day_outside: "text-[#5B3E2E]/20",
            day_disabled: "text-[#5B3E2E]/20 line-through opacity-50",
          }}
        />
        <div className="flex items-center justify-center flex-wrap gap-x-4 gap-y-1 mt-3 text-[10px] text-[#5B3E2E]/55">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#c44a3a]/40" /> Completo</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#95B89A]" /> Día elegido</span>
        </div>
      </div>

      {/* Franjas horarias del día seleccionado */}
      {selectedDate && (
        <div data-testid="session-time-slots" className="bg-[#FDFAF4] rounded-2xl border border-[#5B3E2E]/10 p-4 mb-5 animate-slide-up">
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#5B3E2E]/40 font-semibold mb-3">
            Horarios para {selectedDate.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}
          </p>
          <div className="flex flex-wrap gap-2">
            {selectedItems.map(slot => {
              const isSelected = selectedTime === slot.time;
              const baseClass = "px-4 py-2 rounded-full text-sm font-medium transition-all";              let stateClass;
              let title;
              if (isSelected) stateClass = "bg-[#95B89A] text-white";
              else if (slot.reason === "booked") {
                stateClass = "bg-[#c44a3a]/12 text-[#c44a3a]/85 cursor-not-allowed";
                title = "Franja ya reservada";
              } else if (slot.reason === "blocked") {
                stateClass = "bg-[#c44a3a]/12 text-[#c44a3a]/85 cursor-not-allowed";
                title = "Franja no disponible";
              } else {
                stateClass = "bg-[#5B3E2E]/5 text-[#5B3E2E]/70 hover:bg-[#5B3E2E]/10";
              }
              return (
                <button
                  key={slot.time}
                  data-testid={`session-slot-${slot.date}-${slot.time}`}
                  onClick={() => slot.available && setSelectedTime(slot.time)}
                  disabled={!slot.available}
                  title={title}
                  className={`${baseClass} ${stateClass}`}
                >
                  {slot.time}
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-[#5B3E2E]/45 mt-3 flex items-center gap-1.5">
            <Clock size={10} /> Hora local de Madrid (España)
          </p>
        </div>
      )}

      {selectedDate && selectedTime && (
        <div className="bg-[#FDFAF4] rounded-2xl border border-[#5B3E2E]/10 p-4 mb-5 animate-slide-up">
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#5B3E2E]/40 font-semibold mb-3">Tus datos</p>

          <p className="text-xs text-[#5B3E2E]/60 mb-2">¿Cómo prefieres la sesión?</p>
          <div className="grid grid-cols-2 gap-2 mb-4">
            <button
              data-testid="session-modality-call"
              onClick={() => setModality("call")}
              className={`flex flex-col items-center justify-center gap-1 p-3 rounded-xl border-2 text-sm font-medium transition-all ${modality === "call" ? "border-[#95B89A] bg-[#95B89A]/10 text-[#5A8060]" : "border-[#5B3E2E]/10 text-[#5B3E2E]/60"}`}
            >
              <span className="flex items-center gap-2"><Phone size={15} /> Llamada de teléfono</span>
            </button>
            <button
              data-testid="session-modality-video"
              onClick={() => setModality("video")}
              className={`flex flex-col items-center justify-center gap-1 p-3 rounded-xl border-2 text-sm font-medium transition-all ${modality === "video" ? "border-[#95B89A] bg-[#95B89A]/10 text-[#5A8060]" : "border-[#5B3E2E]/10 text-[#5B3E2E]/60"}`}
            >
              <span className="flex items-center gap-2"><MessageCircle size={15} /> Llamada de WhatsApp</span>
              <span className="text-[10px] font-normal opacity-70">para fuera de España</span>
            </button>
          </div>

          <p className="text-xs text-[#5B3E2E]/60 mb-1.5">{modality === "video" ? "Número de WhatsApp" : "Número de teléfono"} (con prefijo) <span className="text-[#c44a3a]">*</span></p>
          <Input
            data-testid="session-phone-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+34 600 000 000"
            className="bg-white border-[#5B3E2E]/15 rounded-xl text-[#5B3E2E] mb-4"
            style={{ fontSize: "16px" }}
          />

          <p className="text-xs text-[#5B3E2E]/60 mb-1.5">¿Hay algo que quieras compartir antes? <span className="text-[#5B3E2E]/30">(opcional)</span></p>
          <Textarea
            data-testid="session-notes-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Lo que sientas que ayuda contarme antes de hablar…"
            className="bg-white border-[#5B3E2E]/15 rounded-xl text-[#5B3E2E] min-h-[80px]"
            style={{ fontSize: "16px" }}
          />
        </div>
      )}

      {selectedDate && selectedTime && (
        <Button
          data-testid="session-confirm-btn"
          onClick={handleSubmit}
          disabled={submitting || !phone.trim()}
          className="w-full bg-[#E48D57] hover:bg-[#D07C46] text-white rounded-full py-3.5 font-medium disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {submitting ? <Loader size={16} className="animate-spin" /> : null}
          {submitting ? "Reservando…" : "Continuar al pago"}
        </Button>
      )}
    </div>
  );
}

function PaymentScreen({ booking, onBack }) {
  const [processing, setProcessing] = useState(false);

  const handlePay = async () => {
    setProcessing(true);
    try {
      const { data } = await axios.post(
        `${API}/payments/session-checkout/${booking.id}`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("No se pudo iniciar el pago");
        setProcessing(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al iniciar el pago");
      setProcessing(false);
    }
  };

  const dateStr = new Date(booking.date + "T00:00:00").toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div data-testid="session-payment" className="max-w-lg mx-auto px-5 py-6">
      <button onClick={onBack} className="flex items-center gap-2 text-[#5B3E2E]/60 hover:text-[#5B3E2E] mb-5 text-sm">
        <ArrowLeft size={16} /> Volver
      </button>

      <div className="bg-[#FDFAF4] rounded-3xl border border-[#5B3E2E]/8 p-6 mb-5 text-center">
        <p className="text-[10px] tracking-[0.2em] uppercase text-[#5B3E2E]/40 font-semibold mb-3">Resumen de tu sesión</p>
        <h2 className="font-heading text-xl text-[#5B3E2E] mb-3 first-letter:uppercase">{dateStr} · {booking.time}</h2>
        <div className="flex justify-center gap-4 text-sm text-[#5B3E2E]/70 mb-4">
          {booking.modality === "video"
            ? <span className="flex items-center gap-1.5"><MessageCircle size={14} /> Llamada de WhatsApp</span>
            : <span className="flex items-center gap-1.5"><Phone size={14} /> Llamada de teléfono</span>}
          <span>·</span>
          <span data-testid="session-summary-duration">30 min</span>
        </div>
        <div className="border-t border-[#5B3E2E]/8 pt-4 mt-4">
          <p className="text-xs text-[#5B3E2E]/50 mb-1">Total</p>
          <p data-testid="session-summary-price" className="text-3xl font-bold text-[#5B3E2E]">{Number(booking.amount_eur).toFixed(2).replace(".", ",")} €</p>
          <p className="text-[11px] text-[#5B3E2E]/40 mt-1">IVA incluido</p>
        </div>
      </div>

      <div className="bg-[#5B3E2E]/6 rounded-2xl border border-[#5B3E2E]/12 p-3 mb-5 text-center">
        <p className="text-[11px] text-[#5B3E2E]/65">
          Tu franja horaria está reservada durante 15 minutos mientras completas el pago. Serás redirigido a Stripe de forma segura.
        </p>
      </div>

      <Button
        data-testid="session-pay-btn"
        onClick={handlePay}
        disabled={processing}
        className="w-full bg-[#6B9070] hover:bg-[#5A8060] text-white rounded-full py-3.5 font-medium flex items-center justify-center gap-2 shadow-[0_4px_16px_rgba(72,115,76,0.25)]"
      >
        {processing ? <Loader size={16} className="animate-spin" /> : null}
        {processing ? "Redirigiendo…" : `Pagar ${Number(booking.amount_eur).toFixed(2).replace(".", ",")} €`}
      </Button>
    </div>
  );
}

function MySessions({ onBack }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/sessions/mine`, { withCredentials: true })
      .then(r => setRows(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader size={22} className="text-[#E48D57] animate-spin" /></div>;

  return (
    <div data-testid="session-mine" className="max-w-lg mx-auto px-5 py-6">
      <button onClick={onBack} className="flex items-center gap-2 text-[#5B3E2E]/60 hover:text-[#5B3E2E] mb-5 text-sm">
        <ArrowLeft size={16} /> Volver
      </button>
      <h2 className="font-heading text-2xl text-[#5B3E2E] mb-5">Mis sesiones</h2>
      {rows.length === 0 ? (
        <p className="text-[#5B3E2E]/50 text-sm text-center py-10">Aún no has solicitado ninguna sesión.</p>
      ) : (
        <div className="space-y-3">
          {rows.map(b => {
            const dateStr = new Date(b.date + "T00:00:00").toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });
            const labelMap = {
              pending_payment: { text: "Pendiente de pago", color: "bg-[#F0A82C]/15 text-[#a06b00]" },
              confirmed: { text: "Confirmada", color: "bg-[#6B9070]/15 text-[#6B9070]" },
              completed: { text: "Realizada", color: "bg-[#5B3E2E]/10 text-[#5B3E2E]" },
              cancelled: { text: "Cancelada", color: "bg-[#c44a3a]/12 text-[#c44a3a]" },
            };
            const status = labelMap[b.status] || labelMap.pending_payment;
            return (
              <div key={b.id} className="bg-[#FDFAF4] rounded-2xl border border-[#5B3E2E]/10 p-4">
                <div className="flex items-start justify-between gap-3 mb-1.5">
                  <p className="font-medium text-[#5B3E2E] text-sm first-letter:uppercase">{dateStr} · {b.time}</p>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${status.color}`}>{status.text}</span>
                </div>
                <p className="text-[11px] text-[#5B3E2E]/50 flex items-center gap-2">
                  {b.modality === "video" ? <MessageCircle size={12} /> : <Phone size={12} />}
                  {b.modality === "video" ? "Llamada de WhatsApp" : "Llamada de teléfono"} · {b.phone}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function IndividualSessionPage() {
  const [view, setView] = useState("intro"); // intro | book | pay | success | mine
  const [activeBooking, setActiveBooking] = useState(null);

  // Detect Stripe return via query string
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paid = params.get("paid");
    const sessionId = params.get("session_id");
    const cancelled = params.get("cancelled");
    if (cancelled === "1") {
      toast.info("Pago cancelado. Puedes reintentarlo cuando quieras.");
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    if (paid !== "1" || !sessionId) return;
    let mounted = true;
    const poll = async (attempt = 0) => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
        if (!mounted) return;
        if (data.payment_status === "paid") {
          // Fetch confirmed booking to populate success screen
          const { data: mine } = await axios.get(`${API}/sessions/mine`, { withCredentials: true });
          const confirmed = (mine || []).find(b => b.status === "confirmed");
          if (confirmed) {
            setActiveBooking(confirmed);
            setView("success");
          } else {
            setView("mine");
          }
          window.history.replaceState({}, "", window.location.pathname);
          return;
        }
        if (attempt < 8) {
          setTimeout(() => poll(attempt + 1), 1500);
        } else {
          toast.info("Confirmando tu pago… revisa 'Mis sesiones' en unos segundos.");
          setView("mine");
          window.history.replaceState({}, "", window.location.pathname);
        }
      } catch {
        if (attempt < 4) setTimeout(() => poll(attempt + 1), 2000);
      }
    };
    poll();
    return () => { mounted = false; };
  }, []);

  if (view === "book") {
    return <BookingFlow
      onBack={() => setView("intro")}
      onBooked={(b) => { setActiveBooking(b); setView("pay"); }}
    />;
  }
  if (view === "pay" && activeBooking) {
    return <PaymentScreen booking={activeBooking} onBack={() => setView("intro")} />;
  }
  if (view === "success" && activeBooking) {
    return (
      <div data-testid="session-success" className="max-w-lg mx-auto px-5 py-12 text-center">
        <div className="w-16 h-16 rounded-full bg-[#6B9070]/12 flex items-center justify-center mx-auto mb-5">
          <Check size={30} className="text-[#6B9070]" />
        </div>
        <h2 className="font-heading text-2xl text-[#5B3E2E] mb-3">¡Sesión confirmada!</h2>
        <p className="text-[15px] text-[#5B3E2E]/70 leading-relaxed mb-2">
          Te llamaré {activeBooking.modality === "video" ? "por WhatsApp" : "por teléfono"} al <strong>{activeBooking.phone}</strong> el día y hora elegidos.
        </p>
        <p className="text-sm text-[#5B3E2E]/55 mb-8 leading-relaxed">
          Recibirás un recordatorio antes de la sesión. Si necesitas cambiar algo, escríbeme a{" "}
          <a href="mailto:espacio_refugium@outlook.com" className="text-[#E48D57] underline inline-flex items-center gap-1">
            <Mail size={12} /> espacio_refugium@outlook.com
          </a>.
        </p>
        <Button onClick={() => { setView("intro"); setActiveBooking(null); }} className="bg-[#5B3E2E] hover:bg-[#4a3326] text-white rounded-full px-8 py-3">
          Volver al inicio
        </Button>
      </div>
    );
  }
  if (view === "mine") {
    return <MySessions onBack={() => setView("intro")} />;
  }
  return <IntroScreen onStart={() => setView("book")} onSeeMine={() => setView("mine")} />;
}
